import { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StopIcon from '@mui/icons-material/Stop';

import { getTagChipSx } from './tagChipStyles';

interface AskComposerProps {
  variant: 'hero' | 'followup';
  question: string;
  selectedTags: string[];
  availableTags: string[];
  isLoadingTags: boolean;
  isStreaming: boolean;
  isFindingNotes: boolean;
  contextNotesCount: number;
  onQuestionChange: (question: string) => void;
  onTagsChange: (tags: string[]) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const AskComposer = ({
  variant,
  question,
  selectedTags,
  availableTags,
  isLoadingTags,
  isStreaming,
  isFindingNotes,
  contextNotesCount,
  onQuestionChange,
  onTagsChange,
  onSubmit,
  onCancel,
}: AskComposerProps) => {
  const [tagAnchorEl, setTagAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isHero = variant === 'hero';
  const isBusy = isStreaming || isFindingNotes;
  const isSubmitDisabled = isBusy || !question.trim();
  const isTagPopoverOpen = Boolean(tagAnchorEl);

  useEffect(() => {
    if (isHero) {
      inputRef.current?.focus();
    }
  }, [isHero]);

  const handleOpenTagPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTagAnchorEl(event.currentTarget);
    setTagSearchQuery('');
  };

  const handleCloseTagPopover = () => {
    setTagAnchorEl(null);
    setTagSearchQuery('');
  };

  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onTagsChange(selectedTags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !isSubmitDisabled) {
      event.preventDefault();
      onSubmit();
    }
  };

  const filteredAvailableTags = availableTags.filter((tag) => tag.toLowerCase().includes(tagSearchQuery.toLowerCase()));

  const tagScopeRow = (
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
      <Typography variant="caption" color="text.secondary">
        {selectedTags.length > 0 ? 'Searching' : 'Searching your notes'}
      </Typography>
      {selectedTags.map((tag, index) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          onDelete={isBusy ? undefined : () => handleRemoveTag(tag)}
          sx={{ height: 24, fontSize: 12, borderRadius: '4px', ...getTagChipSx(index) }}
        />
      ))}
      <ButtonBase
        onClick={handleOpenTagPopover}
        disabled={isBusy || isLoadingTags}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.375,
          height: 24,
          px: 1.125,
          border: '1px dashed',
          borderColor: 'neutral.dashBorder',
          borderRadius: '4px',
          color: 'text.secondary',
          fontSize: 12,
          fontWeight: 500,
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        tag
      </ButtonBase>
      <Box sx={{ flex: 1 }} />
      {selectedTags.length > 0 && !isLoadingTags && (
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {contextNotesCount} note{contextNotesCount === 1 ? '' : 's'} in scope
        </Typography>
      )}
    </Stack>
  );

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      {!isHero && tagScopeRow}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.paper',
          border: '1px solid #9CA3AF',
          borderRadius: '23px',
          p: isHero ? '8px 8px 8px 20px' : '7px 8px 7px 20px',
          boxShadow: isHero ? '0px 4px 20px 0px rgba(129, 129, 129, 0.12)' : 'none',
          '&:focus-within': { borderColor: 'primary.main' },
        }}
      >
        <InputBase
          inputRef={inputRef}
          multiline
          maxRows={5}
          fullWidth
          placeholder={isHero ? 'Ask your notes…' : 'Ask another question…'}
          value={question}
          onChange={(event) => onQuestionChange(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isBusy}
          sx={{ fontSize: 15, '& .MuiInputBase-input::placeholder': { color: '#6B7280', opacity: 1 } }}
        />
        <IconButton size="small" onClick={handleOpenTagPopover} disabled={isBusy} aria-label="Scope by tag">
          <LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        {isStreaming ? (
          <IconButton
            size="small"
            onClick={onCancel}
            aria-label="Stop"
            sx={{ bgcolor: 'grey.200', '&:hover': { bgcolor: 'grey.300' } }}
          >
            <StopIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            aria-label={isFindingNotes ? 'Finding notes' : 'Ask'}
            sx={{
              bgcolor: 'primary.main',
              color: 'common.white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
            }}
          >
            {isFindingNotes ? <CircularProgress size={18} /> : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        )}
      </Box>
      {isHero && selectedTags.length > 0 && tagScopeRow}
      {isHero && selectedTags.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }} role="status">
          {isFindingNotes ? 'Finding relevant notes…' : 'Just ask — we’ll find relevant notes. Tags are optional.'}
        </Typography>
      )}

      <Popover
        open={isTagPopoverOpen}
        anchorEl={tagAnchorEl}
        onClose={handleCloseTagPopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 320, maxHeight: 400 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search tags..."
            value={tagSearchQuery}
            onChange={(event) => setTagSearchQuery(event.target.value)}
            sx={{ mb: 1.5 }}
          />

          {isLoadingTags ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : filteredAvailableTags.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
              No tags found
            </Typography>
          ) : (
            <Stack spacing={0.5} sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {filteredAvailableTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Box
                    key={tag}
                    onClick={() => {
                      if (!isBusy) handleTagToggle(tag);
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <LocalOfferOutlinedIcon fontSize="small" color={isSelected ? 'primary' : 'inherit'} />
                      <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                        {tag}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Popover>
    </Stack>
  );
};

export default AskComposer;
